import { useState, useRef, useMemo } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Star, Search, Filter, ChevronDown, ChevronUp, Quote } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import reviewsData from '@/data/reviews.json';

const filterOptions = ['All', '5 Stars', '4 Stars', 'With Text'];

export default function ReviewsSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [visibleCount, setVisibleCount] = useState(9);
  const [expandedReview, setExpandedReview] = useState(null);
  
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Filter reviews with text only for display
  const reviewsWithText = useMemo(() => {
    return reviewsData.filter(r => r.text && r.text.trim().length > 0);
  }, []);

  const filteredReviews = useMemo(() => {
    let filtered = reviewsWithText;

    // Apply star filter
    if (activeFilter === '5 Stars') {
      filtered = filtered.filter(r => r.stars === 5);
    } else if (activeFilter === '4 Stars') {
      filtered = filtered.filter(r => r.stars === 4);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        r => r.name.toLowerCase().includes(query) || 
             r.text.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [reviewsWithText, activeFilter, searchQuery]);

  const visibleReviews = filteredReviews.slice(0, visibleCount);
  const hasMore = visibleCount < filteredReviews.length;

  const stats = useMemo(() => {
    const fiveStars = reviewsData.filter(r => r.stars === 5).length;
    const fourStars = reviewsData.filter(r => r.stars === 4).length;
    const avgRating = reviewsData.reduce((sum, r) => sum + r.stars, 0) / reviewsData.length;
    return { total: reviewsData.length, fiveStars, fourStars, avgRating: avgRating.toFixed(1) };
  }, []);

  return (
    <section 
      ref={ref}
      id="reviews"
      className="py-20 md:py-32 bg-[#0B111E] relative"
      data-testid="reviews-module"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 glass px-6 py-3 rounded-full mb-6">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" 
              alt="Google" 
              className="w-6 h-6"
            />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#FBBF24] text-[#FBBF24]" />
              ))}
            </div>
            <span className="text-xl font-bold text-white">{stats.avgRating}</span>
            <span className="text-[#94A3B8]">({stats.total} reviews)</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            What Our Customers Say
          </h2>
          
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Don't just take our word for it — see what Blue Mountains homeowners have to say
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-6 md:gap-12 mb-10"
        >
          <div className="text-center">
            <div className="text-3xl font-bold text-[#FBBF24]">{stats.fiveStars}</div>
            <div className="text-sm text-[#94A3B8]">5-Star Reviews</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#38BDF8]">{stats.fourStars}</div>
            <div className="text-sm text-[#94A3B8]">4-Star Reviews</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#F95738]">100%</div>
            <div className="text-sm text-[#94A3B8]">Satisfaction</div>
          </div>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4 mb-10"
        >
          <div className="relative flex-1 max-w-md mx-auto sm:mx-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <Input
              placeholder="Search reviews..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#111A2E] border-white/10 text-white placeholder:text-[#94A3B8] h-12"
              data-testid="reviews-search-input"
            />
          </div>
          
          <div className="flex gap-2 justify-center flex-wrap">
            {filterOptions.map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? "default" : "outline"}
                onClick={() => setActiveFilter(filter)}
                className={`${
                  activeFilter === filter
                    ? 'bg-[#38BDF8] text-white hover:bg-[#38BDF8]/90'
                    : 'bg-transparent border-white/10 text-[#94A3B8] hover:bg-white/5 hover:text-white'
                }`}
              >
                {filter}
              </Button>
            ))}
          </div>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visibleReviews.map((review, index) => (
              <motion.div
                key={review.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="review-card glass rounded-2xl p-6 border border-transparent hover:border-[#38BDF8]/30"
                data-testid="google-review-card"
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <Avatar className="w-12 h-12 border-2 border-[#38BDF8]/30">
                    <AvatarImage 
                      src={`/avatars/${review.profile_image.split('/').pop()}`} 
                      alt={review.name}
                    />
                    <AvatarFallback className="bg-[#38BDF8]/20 text-[#38BDF8]">
                      {review.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white truncate">{review.name}</h4>
                    <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                      <span>{review.review_count}</span>
                      <span>·</span>
                      <span>{review.date}</span>
                    </div>
                  </div>
                  
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" 
                    alt="Google" 
                    className="w-5 h-5 opacity-60"
                  />
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-4 h-4 ${
                        i < review.stars 
                          ? 'fill-[#FBBF24] text-[#FBBF24]' 
                          : 'text-[#94A3B8]/30'
                      }`}
                    />
                  ))}
                </div>

                {/* Review Text */}
                <div className="relative">
                  <Quote className="absolute -top-1 -left-1 w-6 h-6 text-[#38BDF8]/20" />
                  <p className={`text-sm text-[#94A3B8] leading-relaxed pl-4 ${
                    !expandedReview || expandedReview !== review.id 
                      ? 'line-clamp-4' 
                      : ''
                  }`}>
                    {review.text}
                  </p>
                  
                  {review.text.length > 200 && (
                    <button
                      onClick={() => setExpandedReview(
                        expandedReview === review.id ? null : review.id
                      )}
                      className="flex items-center gap-1 text-[#38BDF8] text-sm font-medium mt-2 hover:underline"
                    >
                      {expandedReview === review.id ? (
                        <>Show less <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>Read more <ChevronDown className="w-4 h-4" /></>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Load More */}
        {hasMore && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-12"
          >
            <Button
              onClick={() => setVisibleCount(prev => prev + 9)}
              variant="outline"
              className="px-8 py-6 border-[#38BDF8]/30 text-[#38BDF8] hover:bg-[#38BDF8]/10"
            >
              Load More Reviews ({filteredReviews.length - visibleCount} remaining)
            </Button>
          </motion.div>
        )}

        {/* No Results */}
        {filteredReviews.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-[#94A3B8]">No reviews match your search criteria.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
